/** @file Checks the drawables of a bundle puppet: nodes, entry tables, rect counts, sheets and bounds. */
import { tileCountOf } from '@zilo/pose3d-format/tables/tileCountOf';
import type { BundleError } from '@zilo/pose3d-export/check/bundleError';
import type { CellRect, ExportPuppet, ResolvedEntry } from '@zilo/pose3d-export/types/bundle';

const inside = ([x, y, w, h]: CellRect, [sw, sh]: [number, number]): boolean => x >= 0 && y >= 0 && w > 0 && h > 0 && x + w <= sw && y + h <= sh;

/** `rects` (required) and the drawn-angle arrays (when present) have one item per tile. */
function checkLengths(entry: ResolvedEntry, tiles: number, path: string, errors: BundleError[]): boolean {
  let rectsOk = true;
  for (const key of ['rects', 'drawnAzimuths', 'drawnScreenAngles'] as const) {
    const list = entry[key];
    if ((key === 'rects' || list !== undefined) && (!Array.isArray(list) || list.length !== tiles)) {
      errors.push({ code: 'BUNDLE_FORMAT', path: `${path}/${key}`, message: `${key} must have ${tiles} items (table ${entry.yaw})` });
      rectsOk &&= key !== 'rects';
    }
  }
  return rectsOk;
}

function checkEntry(puppet: ExportPuppet, entry: ResolvedEntry, path: string, errors: BundleError[], badTables: ReadonlySet<string>): void {
  const table = Object.hasOwn(puppet.tables, entry.yaw) ? puppet.tables[entry.yaw] : undefined;
  if (badTables.has(entry.yaw)) return;
  if (table === undefined) {
    errors.push({ code: 'BUNDLE_TABLE_UNKNOWN', path: `${path}/yaw`, message: `table ${entry.yaw} is not in puppet.tables` });
    return;
  }
  if (!checkLengths(entry, tileCountOf(table), path, errors) || !entry.rects.some((r) => r !== null)) return;
  const sheet = Object.hasOwn(puppet.sheets, entry.sheet) ? puppet.sheets[entry.sheet] : undefined;
  if (sheet === undefined) {
    errors.push({ code: 'BUNDLE_SHEET_UNKNOWN', path: `${path}/sheet`, message: `sheet ${entry.sheet} is not in puppet.sheets` });
    return;
  }
  entry.rects.forEach((r, t) => {
    if (r !== null && !inside(r, sheet.size)) {
      errors.push({ code: 'BUNDLE_RECT_OUTSIDE', path: `${path}/rects/${t}`, message: `rect [${r.join(', ')}] is outside sheet ${entry.sheet} [${sheet.size.join(', ')}]` });
    }
  });
}

/**
 * Errors of `puppet.drawables`: an unknown `node` or `anchor.node` (`BUNDLE_NODE_UNKNOWN`), an entry `yaw` missing from
 * `puppet.tables` (`BUNDLE_TABLE_UNKNOWN`), per-tile arrays of the wrong length (`BUNDLE_FORMAT`), a sheet missing
 * from `puppet.sheets` (`BUNDLE_SHEET_UNKNOWN`), a rect outside its sheet (`BUNDLE_RECT_OUTSIDE`); entries on a
 * table in `badTables` (already reported malformed) are skipped.
 */
export function checkBundleDrawables(puppet: ExportPuppet, badTables: ReadonlySet<string> = new Set()): BundleError[] {
  const errors: BundleError[] = [];
  puppet.drawables.forEach((d, i) => {
    const path = `/puppet/drawables/${i}`;
    const nodes: [string, string | undefined][] = [[`${path}/node`, d.node], [`${path}/anchor/node`, d.kind === 'impostor' ? d.anchor?.node : undefined]];
    for (const [p, n] of nodes) {
      if (n !== undefined && !Object.hasOwn(puppet.nodes, n)) errors.push({ code: 'BUNDLE_NODE_UNKNOWN', path: p, message: `${p} names unknown node ${n}` });
    }
    if (d.kind === 'impostor') d.facets.forEach((e, k) => { checkEntry(puppet, e, `${path}/facets/${k}`, errors, badTables); });
    if (d.kind === 'effect' && !Object.hasOwn(puppet.sheets, d.frames.sheet)) {
      errors.push({ code: 'BUNDLE_SHEET_UNKNOWN', path: `${path}/frames/sheet`, message: `sheet ${d.frames.sheet} is not in puppet.sheets` });
    }
  });
  return errors;
}
